import { startOfWeek } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { enGB } from 'react-day-picker/locale';
import { useDispatch, useSelector } from 'react-redux';
import { minPricesMonthsSelector, minPricesSelector } from '../../redux/data/cachedAvail/selectors';
import {
  isArrivalDateAvailableSelector,
  searchFormArrivalDateDateSelector,
  searchFormChannelIdSelector,
  searchFormDurationSelector,
  searchFormPartySizeSelector,
  searchFormPetFriendlySelector,
  searchFormRegionIdSelector,
} from '../../redux/search/form/selectors';
import { getFilteredRegionDates } from '../../utils/cachedAvail';
import { convertDateStringToDate } from '../../utils/utils';
import { CustomNextButton, CustomPreviousButton } from './DatePickerComponents';
import { dateToDDMMYYYY } from '../../utils/date';
import { setIsArrivalDateAvailable } from '../../redux/search/form/actions';

export const DatePicker = ({ handleDateChange }) => {
  const dispatch = useDispatch();
  const channelId = useSelector(searchFormChannelIdSelector);
  const minPrices = useSelector(minPricesSelector);
  const partySize = useSelector(searchFormPartySizeSelector);
  const selectedRegionId = useSelector(searchFormRegionIdSelector);
  const petFriendly = useSelector(searchFormPetFriendlySelector);
  const duration = parseInt(useSelector(searchFormDurationSelector));
  const months = useSelector(minPricesMonthsSelector);
  const arrivalDate = useSelector(searchFormArrivalDateDateSelector);
  const isArrivalDateAvailable = useSelector(isArrivalDateAvailableSelector);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const markCheapestPerWeek = useCallback((items) => {
    const groups = items.reduce((acc, item) => {
      const date = convertDateStringToDate(item.date);
      const weekStart = getWeekStart(date);

      if (!acc[weekStart]) acc[weekStart] = [];
      acc[weekStart].push(item);

      return acc;
    }, {});

    // For each week, find min price and mark cheapest
    Object.values(groups).forEach((weekItems) => {
      const minPrice = Math.min(...weekItems.map((i) => Number(i.price)));

      weekItems.forEach((i) => {
        i.cheapest = Number(i.price) === minPrice;
      });
    });

    return Object.values(groups).flat();
  }, []);

  const filteredHolidaysWithCheapest = useMemo(() => {
    if (!minPrices) return [];

    const whichDataToUse =
      selectedRegionId === 'all-regions' ? Object.values(minPrices).flat() : minPrices[selectedRegionId] || [];

    if (whichDataToUse.length === 0) return [];

    const filteredHolidays = getFilteredRegionDates(whichDataToUse, duration, petFriendly, partySize);

    return markCheapestPerWeek(filteredHolidays);
  }, [duration, markCheapestPerWeek, minPrices, partySize, petFriendly, selectedRegionId]);

  const formattedArrivalDate = useMemo(() => dateToDDMMYYYY(arrivalDate), [arrivalDate]);

  const arrivalDateAvailable =
    channelId === 'ukcaravan'
      ? filteredHolidaysWithCheapest.some(({ date }) => date === formattedArrivalDate)
      : toDateOnly(arrivalDate) >= toDateOnly(tomorrow);

  useEffect(() => {
    if (isArrivalDateAvailable !== arrivalDateAvailable) {
      setIsArrivalDateAvailable(dispatch, arrivalDateAvailable);
    }
  }, [arrivalDateAvailable, dispatch, isArrivalDateAvailable]);

  const findPriceForDate = (targetDate) => {
    const formatDate = (date) => date.toLocaleDateString('en-GB'); // gives "DD/MM/YYYY"
    const formattedTarget = formatDate(targetDate);
    return filteredHolidaysWithCheapest.find(({ date }) => date === formattedTarget);
  };

  // Get the last end month
  const lastMonthStr = months[months.length - 1]; // e.g. '2027-01'
  const [year, month] = lastMonthStr.split('-').map(Number);
  const endMonthDate = new Date(year, month - 1);

  return (
    <DayPicker
      mode="single"
      required
      locale={enGB}
      onSelect={handleDateChange}
      calendarStartDay={2}
      startMonth={new Date()}
      endMonth={endMonthDate}
      defaultMonth={arrivalDate}
      selected={arrivalDate}
      navLayout="around"
      fixedWeeks
      disabled={[
        { before: tomorrow },
        (date) => (channelId === 'ukcaravan' ? !findPriceForDate(date) : ''), // disable days without a price
      ]}
      components={{
        PreviousMonthButton: CustomPreviousButton,
        NextMonthButton: CustomNextButton,
        DayButton: ({ day, ...buttonProps }) => {
          const actualDate = day?.date instanceof Date ? day.date : day;
          const data = findPriceForDate(actualDate);
          const price = data?.price;

          return (
            <button {...buttonProps}>
              <div className="text-center day-picker">
                <p className="day">{actualDate.getDate()}</p>
                {price && <p className={`price ${data?.cheapest ? 'cheap' : ''}`}>£{Number(price || 0).toFixed(0)}</p>}
              </div>
            </button>
          );
        },
      }}
    />
  );
};

const getWeekStart = (date) => startOfWeek(date, { locale: enGB });

const toDateOnly = (dateStr) => {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d;
};
